import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { isObject, isString } from 'lodash';
import { defineStore } from 'pinia';

import { fetchAllRegisteredDevices, fetchLanguageOptions as fetchLanguageOptionsAPI } from '@/api/apiService';
import api from '@/api/index';
import dayjs from '@/plugins/dayjs';
import { debugLog } from '@/static/develop.js';
import { getLanguageByCode } from '@/static/languages';
import { useMicrophoneStore } from '@/stores/microphone';
import { useToastStore } from '@/stores/toast';
import { useUserStore } from '@/stores/user';
import { LanguageCode } from '@/types/common';
import { Language } from '@/types/common';
import { VoiceStreamType } from '@/types/components';
import { IMessageVc } from '@/types/socket';
import { MessageLd } from '@/types/socket';
import { getInoutDevices } from '@/utils/device';

// 오디오 클립(데이터) 관리, Audio Player 컴포넌트를 통해 재생
export const useAudioStore = defineStore('audio', () => {
  const { t } = useI18n();
  const toastStore = useToastStore();
  const userStore = useUserStore();
  const microphoneStore = useMicrophoneStore();

  const micInputByte = ref([]); //마이크 음성바이트 임시 저장
  const micSampleRate = ref(16000); //저장된 마이크 샘플레이트

  const isMicOn = ref(false); // 마이크 활성화 여부
  const isMicPaused = ref(false); // 마이크 일시정지 여부
  const isSoundOn = ref(false);
  const isSingleLanguage = ref(false);
  const isTssOn = ref(true);

  const audioDownloading = ref(null);
  const isMutedByEmployee = ref(false);
  const languageSelected = ref<Language>();
  const languageDetected = ref<MessageLd>();
  const playVolume = ref(0.2);
  const voiceStatus = ref<VoiceStreamType>('PENDING');

  const manualAudioKeys = ref<string[]>([]); //재생할 내 Audio URL (수동 재생, 모든 audio 대상)
  const playCallLogId = ref('');
  const playState = ref(0); //state : 0 정지 UI표시, 1 (원본-soure 재생 UI표시), 2 (번역본-target 재생 UI표시)
  const humanVoiceIsTooSmall = ref(false);
  const gainNode = ref(null);
  const playlistAuto = ref([]); // 자동재생 오디오가 재생되어야 할 올바른 순서
  const playlistAutoFinished = ref([]); // 자동재생 오디오가 재생된 순서
  const playlistAutoQueue = ref([]); // 자동재생 오디오 재생 순서
  const playlistAutoQueuePending = ref([]); // 자동재생 오디오 수신 순서
  let isProcessingQueue = false; // 임시큐 처리 중 여부

  const audioInfoMap = ref<Map<string, { isTarget: string; callLogId: string }>>(new Map());
  const selectedInput = ref(null);
  const selectedOutput = ref(null);
  const devices = ref(null);

  const languageHome = ref(); // [싱글모드] 나의 언어
  const languageAway = ref(); // [싱글모드] 상대 언어

  const languageOptions = ref([]);

  // 띄어쓰기 없는 언어인지
  const isLanguageNoSpace = computed(() => ['zh', 'ja', 'th'].includes(languageSelected.value?.code ?? ''));

  // 새로 추가된 입력장치가 있는가
  const isPresentNewDevice = computed(() => {
    return devices.value?.input?.some((item) => item.newDevice === true);
  });

  const inputManuallyChanged = ref(false);

  const getIdentifierAudio = (audioDetails) => {
    const { callLogId, chatSeqSubId, logFlag } = audioDetails;
    return `${callLogId}-${chatSeqSubId}-${logFlag}`;
  };

  const setLanguageHome = (language: Language) => {
    languageHome.value = language;
  };

  const setLanguageAway = (language: Language) => {
    languageAway.value = language;
  };

  // 자동재생 오디오 파일 큐에 오디오 파일 추가
  const addToPlaylistAutoQueue = async (audioNew) => {
    playlistAutoQueuePending.value.push(audioNew); // 임시 큐에 담음
    await processPendingAutoQueue();
  };

  const fetchWithRetry = async (url, retries = 7, retryDelay = 300) => {
    let audio = null;
    for (let attempt = 1; attempt <= retries && !audio; attempt++) {
      await api
        .get(url)
        .then(({ data, status }) => {
          if (status !== 200 || !data) {
            throw new Error();
          }
          audio = data;
        })
        .catch(async () => {
          console.warn(`[RETRY] 오디오 파일 조회가 ${attempt}회 실패했습니다, ${retryDelay}ms 후 재시도 합니다...`);
          new Promise((resolve) => setTimeout(resolve, retryDelay));
        });
    }
    return audio;
  };

  // 임시 큐를 순서대로 정렬 및 처리
  const processPendingAutoQueue = async () => {
    if (isProcessingQueue) return;
    isProcessingQueue = true;

    const pending = playlistAutoQueuePending.value.slice();
    playlistAutoQueuePending.value = [];

    const playlist = [];
    const failed = [];

    try {
      for (const item of pending) {
        const { callLogId, voiceKey: audioKey, chatId } = item || {};
        if (!callLogId || !audioKey || !chatId) {
          failed.push({ item, reason: 'missing fields' });
          continue;
        }

        try {
          const data = await fetchWithRetry(`/api/chat/business/call/voice/${chatId}/${audioKey}`);

          playlist.push({
            ...item,
            audioKey,
            src: data,
          });
        } catch (e) {
          console.warn('[processPendingAutoQueue] fetch failed', {
            callLogId,
            audioKey,
            chatId,
            error: e,
          });
          failed.push({ item, reason: 'fetch failed', error: e });
        }
      }

      if (playlist.length) {
        playlistAutoQueue.value.push(...playlist);
      }

      if (failed.length) {
        playlistAutoQueuePending.value = failed.map((f) => f.item).concat(playlistAutoQueuePending.value);
      }
    } finally {
      isProcessingQueue = false;
    }
  };

  const toggleMic = (onOff: boolean | null = null) => {
    if (onOff !== null) {
      isMicOn.value = onOff;
    } else {
      isMicOn.value = !isMicOn.value;
    }
    controllVolume();
  };

  // 컨퍼런스 청자일 때만 실행
  const forceMicOff = () => {
    isMicOn.value = false;
  };

  const mutedByEmployee = (muted) => {
    isMutedByEmployee.value = muted;
    if (!muted) {
      controllVolume();
    }
  };

  const controllVolume = () => {
    if (microphoneStore.isMicOn) {
      playVolume.value = 0.2;
    } else {
      playVolume.value = 1.0;
    }
  };

  const toggleSound = () => {
    isSoundOn.value = !isSoundOn.value;
    if (isSoundOn.value) {
      setPlayState(0, '');
      toastStore.info(t('toast_sound_on'), 'sound');
    } else toastStore.info(t('toast_sound_off'), 'sound');
    controllVolume();
  };

  const selectLanguage = (languageCode: LanguageCode, isPersistent: boolean = true) => {
    if (isPersistent) {
      localStorage.setItem('language', languageCode);
    }

    dayjs.locale(languageCode === 'ko' ? 'ko' : 'en');
    languageSelected.value = getLanguageByCode(languageCode);
  };

  //url 다수를 받아 하나씩 player에 넘김
  const setAudioManulSrcs = (isTarget, callLogId, srcs) => {
    //유저가 같은 소스의 오디오를 수동으로 반복 재생시킬 수도 있음
    //이때 무조건 재생시키기 위해 url 강제변경 -> watch 를 통한 AudioPlayer의 재생을 발생시킴
    //wath의 동작을 위해서이기도 하지만 audio 태그의 load 반복은 url이 변경되야 정상 작동함
    const timestamp = Date.now();
    const randomNum = Math.floor(Math.random() * timestamp);

    const sourcesFormatted = srcs.map((srcItem) => {
      const queryString = new URLSearchParams({
        isTarget: isTarget ? '1' : '0',
        callLogId,
        chatId: String(srcItem.chatId),
        voiceKey: String(srcItem.voiceKey),
        r: String(randomNum),
      }).toString();
      return `${srcItem.chatId}/${srcItem.voiceKey}?${queryString}`;
    });
    manualAudioKeys.value = [...sourcesFormatted];

    debugLog('set manual urls : ' + manualAudioKeys.value);
  };

  //재생 상태, 중지 처리
  const setPlayState = (state, callLogId) => {
    playState.value = state;
    playCallLogId.value = callLogId;
  };

  const clearAll = () => {
    manualAudioKeys.value = [];
    playlistAutoQueue.value = [];
  };

  const isSameInput = (newInput) => {
    if (!newInput || !selectedInput.value) return false;
    return newInput.id === selectedInput.value.id && newInput.label === selectedInput.value.label;
  };

  async function fetchDevices() {
    const currentDevices = await getInoutDevices(devices.value);
    devices.value = currentDevices;
    return currentDevices;
  }

  async function initDevices() {
    const currentDevices = await getInoutDevices(devices.value);

    // TODO authToken 이 있거나 게스트일 경우 아래 로직 수행. userId 존재여부 보다 확실한 구분이 필요
    if (localStorage.getItem('authToken') || userStore.userId) {
      // 음성 정보가 있는 장치만 가져오기
      const deviceWithVoices = await fetchAllRegisteredDevices();

      if (!deviceWithVoices.success) return;
      // 음성 정보가 있는 장치 중 지금 사용 가능한 장치만 필터링
      const deviceWithVoicesFiltered = deviceWithVoices.data.filter((voiceDevice) =>
        currentDevices.input.some((device) => voiceDevice.deviceLabel.includes(device.label))
      );

      // 입력장치에 음성 정보 추가
      currentDevices.input = currentDevices.input.map((device) => {
        const savedInfo = deviceWithVoicesFiltered.find((info) => info.deviceLabel.includes(device.label));

        const voiceUrl = savedInfo ? savedInfo.voiceUrl : null;
        const voiceId = savedInfo ? savedInfo.deviceId : null;

        return {
          ...device,
          voiceUrl,
          voiceId,
          fetchingVoiceInfo: false,
        };
      });
    }

    // 디폴트 입력장치 설정
    const defaultInput = currentDevices.input.find((input) => input.default);
    const targetInput = selectedInput.value
      ? currentDevices.input.find((i) => i.id === selectedInput.value.id) || defaultInput
      : defaultInput;

    if (targetInput) {
      selectedInput.value = targetInput;
    }

    if (!selectedOutput.value) {
      selectedOutput.value = currentDevices.output.find((o) => o.default);
    }

    devices.value = currentDevices;
  }

  function isInputSelected() {
    const selected = selectedInput.value;
    return Boolean(selected && typeof selected.id === 'string' && selected.id.trim() !== '');
  }

  const handleMessageVoiceStatus = (message: VoiceStreamType | IMessageVc) => {
    let status: VoiceStreamType = 'PENDING';

    if (isString(message)) {
      status = message;
    } else if (isObject(message)) {
      const { status: messageStatus = 'PENDING' } = message;
      status = messageStatus;
    }

    voiceStatus.value = status;
  };

  const fetchLanguageOptions = async () => {
    try {
      const { data } = await fetchLanguageOptionsAPI();
      languageOptions.value = data;
    } catch (err) {
      console.error('failed to fetch language options:', err);
    }
  };

  watch(
    isSoundOn,
    (newVal) => {
      debugLog('isSoundOn', newVal);

      if (!newVal) {
        if (playState.value !== 0) {
          setPlayState(0, '');
        }
      }
    },
    { immediate: true }
  );

  return {
    micInputByte,
    micSampleRate,
    isMicOn,
    isMicPaused,
    isSoundOn,
    isTssOn,
    isSingleLanguage,
    audioDownloading,
    languageSelected,
    languageDetected,
    languageHome,
    languageAway,
    manualAudioKeys,
    playCallLogId,
    playState,
    playVolume,
    playlistAuto,
    playlistAutoFinished,
    playlistAutoQueue,
    humanVoiceIsTooSmall,
    gainNode,
    selectedInput,
    selectedOutput,
    devices,
    isPresentNewDevice,
    inputManuallyChanged,
    isMutedByEmployee,
    voiceStatus,
    playlistAutoQueuePending,
    isLanguageNoSpace,
    audioInfoMap,
    getIdentifierAudio,
    addToPlaylistAutoQueue,
    toggleMic,
    forceMicOff,
    mutedByEmployee,
    toggleSound,
    selectLanguage,
    setAudioManulSrcs,
    setPlayState,
    controllVolume,
    clearAll,
    isSameInput,
    initDevices,
    fetchDevices,
    isInputSelected,
    handleMessageVoiceStatus,
    fetchLanguageOptions,
    languageOptions,
    setLanguageHome,
    setLanguageAway,
  };
});
